/**
 * The sidebar's `+ Add` menu (spec M0-F4 §7.4 opener, M1-B1 owns every entry, order and
 * wording corrected in B10 from `[c:sidebar-add-menu]`).
 *
 * monday's menu is **240 px** wide with 32 px rows and three groups:
 *
 *   Agent · Vibe app · Board · Doc · Dashboard · Workflow · Project management
 *   ─
 *   Folder · Template center · More
 *   ─
 *   New workspace
 *
 * Round 3 had invented a `Board ▸` submenu (New Board · New multi-level board · Start with
 * template); monday has no such submenu — `Board` creates a board and the template gallery is
 * its own entry, **Template center**. The five monday-only rows (Agent, Vibe app, Workflow,
 * Project management, More) are **absent**, per the §3.2 rule.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';
import { CreateBoardDialog } from './CreateBoardDialog';
import { NameDialog } from './NameDialog';
import { TemplatePicker } from '../templates';
import { useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation } from './hooks';
import { useFolderMutations, useWorkspaceMutations } from './workspaces';

export const ADD_MENU_WIDTH = 240;

type AddDialog = 'board' | 'template' | 'folder' | 'workspace' | null;

export function AddMenu({
  workspaceId,
  x,
  y,
  onClose,
}: {
  workspaceId: string | null;
  x: number;
  y: number;
  onClose(): void;
}) {
  const [dialog, setDialog] = useState<AddDialog>(null);
  const folders = useFolderMutations();
  const workspaces = useWorkspaceMutations();
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();
  const navigate = useNavigate();

  /**
   * `Menu` closes itself after a non-submenu entry, and this component **is** the menu's
   * owner — so a naive `onClose` would unmount the dialog the entry just asked for, before
   * it ever rendered. The ref records that a dialog was chosen, and only an untouched close
   * (Escape, an outside click) reaches the parent.
   */
  const chose = useRef(false);
  const closeMenu = () => {
    if (!chose.current) onClose();
  };
  const choose = (next: NonNullable<AddDialog>) => () => {
    chose.current = true;
    setDialog(next);
  };

  /**
   * B10: `docs.create` (M11) and `dashboards.create` (M10) both shipped, so these two entries
   * are real. A dashboard **requires** a workspace (`createDashboardInput.workspaceId`), so the
   * entry is disabled outside one; a doc accepts a null workspace.
   */
  const idOf = (row: unknown): string | null =>
    row && typeof row === 'object' && 'id' in row ? String((row as { id: string | number }).id) : null;

  const createDoc = () => {
    chose.current = true;
    void client.docs.create
      .mutate({ name: 'New doc', workspaceId })
      .then(async (doc) => {
        await invalidate();
        onClose();
        const id = idOf(doc);
        if (id) navigate(`/docs/${id}`);
      })
      .catch(() => onClose());
  };

  const createDashboard = () => {
    if (workspaceId === null) return;
    chose.current = true;
    void client.dashboards.create
      .mutate({ name: 'New dashboard', workspaceId })
      .then(async (dashboard) => {
        await invalidate();
        onClose();
        const id = idOf(dashboard);
        if (id) navigate(`/dashboards/${id}`);
      })
      .catch(() => onClose());
  };

  const entries: MenuEntry[] = [
    { id: 'board', label: 'Board', onSelect: choose('board'), 'data-testid': 'add-board' },
    { id: 'doc', label: 'Doc', onSelect: createDoc, 'data-testid': 'add-doc' },
    {
      id: 'dashboard',
      label: 'Dashboard',
      disabled: workspaceId === null,
      onSelect: createDashboard,
      'data-testid': 'add-dashboard',
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'folder',
      label: 'Folder',
      disabled: workspaceId === null,
      onSelect: choose('folder'),
      'data-testid': 'add-folder',
    },
    { id: 'template', label: 'Template center', onSelect: choose('template'), 'data-testid': 'add-template' },
    { id: 'd2', label: '', divider: true },
    { id: 'workspace', label: 'New workspace', onSelect: choose('workspace'), 'data-testid': 'add-workspace' },
  ];

  if (dialog === 'board') {
    return <CreateBoardDialog workspaceId={workspaceId} onClose={onClose} />;
  }
  if (dialog === 'template') {
    return <TemplatePicker workspaceId={workspaceId} onClose={onClose} />;
  }
  if (dialog === 'folder' && workspaceId) {
    return (
      <NameDialog
        title="Create folder"
        label="Folder name"
        testId="create-folder-dialog"
        onCancel={onClose}
        onSubmit={(name) => {
          // Await before closing: an unmount + navigation can abort an in-flight POST.
          void folders.create.mutateAsync({ workspaceId, name }).then(onClose);
        }}
      />
    );
  }
  if (dialog === 'workspace') {
    return (
      <NameDialog
        title="New workspace"
        label="Workspace name"
        testId="create-workspace-dialog"
        onCancel={onClose}
        onSubmit={(name) => {
          void workspaces.create.mutateAsync({ name }).then(onClose);
        }}
      />
    );
  }

  return <Menu entries={entries} x={x} y={y} width={ADD_MENU_WIDTH} label="Add" onClose={closeMenu} />;
}
